import React, { useEffect, useState, useRef } from "react";
import { toast } from "react-toastify";
import downIcon from "../../../assets/down-icon.svg";
import deleteIcon from "../../../assets/delete-red.svg";
import closeIcon from "../../../assets/crossIcon.svg";
import searchIcon from "../../../assets/search-new-icon.svg";
import RemoveMapping from "./RemoveMapping";
import ConfirmRemoveMapping from "./ConfirmRemoveMapping";
import { FilterDropdown } from "./FilterDropdown";
import { ProcedureFlowServices } from "../../../services/ProcedureFlowServices";
import { handleApiErrorResponses } from "../../../utils/ApisErrorHandling";
import mergeIcon from "../../../assets/merge.svg";
import addButtonSymbol from "../../../assets/addButtonSymbol.svg";

/* ===================================================== */

export default function DefaultDocumentMappingPage() {
  const [mode, setMode] = useState("FILTER");
  const [openRemoveModal, setOpenRemoveModal] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [selectedToRemove, setSelectedToRemove] = useState([]);
  const [schemes, setSchemes] = useState([]);
  const [loadingSchemes, setLoadingSchemes] = useState(false);
  const [selectedScheme, setSelectedScheme] = useState("");
  const [documentTypes, setDocumentTypes] = useState([]);
  const [loadingDocumentTypes, setLoadingDocumentTypes] = useState(false);
  const [selectedDocumentType, setSelectedDocumentType] = useState("");
  const [caseType, setCaseType] = useState([]);
  const [loadingcaseType, setLoadingcaseType] = useState(false);
  const [selectedCaseType, setSelectedCaseType] = useState("");
  const [specialties, setSpecialties] = useState([]);
  const [selectedSpecialty, setSelectedSpecialty] = useState(null);
  const [loadingSpecialties, setLoadingSpecialties] = useState(false);
  const [documentNameList, setDocumentNameList] = useState([]);
  const [loadingDocumentNames, setLoadingDocumentNames] = useState(false);
  const [selectedDocumentName, setSelectedDocumentName] = useState("");

  /* Fetch and Copy block – independent filter state */
  const [fetchCopyScheme, setFetchCopyScheme] = useState("");
  const [fetchCopySpecialty, setFetchCopySpecialty] = useState(null);
  const [fetchCopyDocumentType, setFetchCopyDocumentType] = useState("");
  const [fetchCopyDocumentName, setFetchCopyDocumentName] = useState("");
  const [fetchCopyCaseType, setFetchCopyCaseType] = useState("");
  const [fetchCopyDocumentNameList, setFetchCopyDocumentNameList] = useState([]);
  const [loadingFetchCopyDocumentNames, setLoadingFetchCopyDocumentNames] = useState(false);

  const [admissionDocuments, setAdmissionDocuments] = useState([]);
  const [enhancementDocuments, setEnhancementDocuments] = useState([]);
  const [dischargeDocuments, setDischargeDocuments] = useState([]);

  const [openAddDocumentModal, setOpenAddDocumentModal] = useState(false);
  const [addDocumentSection, setAddDocumentSection] = useState(null);
  const [selectedDocumentsForAdd, setSelectedDocumentsForAdd] = useState([]);
  const [savingMapping, setSavingMapping] = useState(false);
  const [savingFromHeader, setSavingFromHeader] = useState(false);
  const [loadingFetchedDocs, setLoadingFetchedDocs] = useState(false);
  const [removingMapping, setRemovingMapping] = useState(false);

  /* Two-column layout: data fetched from second filter (Fetch and Copy) */
  const [showTwoColumnLayout, setShowTwoColumnLayout] = useState(false);
  const [fetchCopyAdmissionDocuments, setFetchCopyAdmissionDocuments] = useState([]);
  const [fetchCopyEnhancementDocuments, setFetchCopyEnhancementDocuments] = useState([]);
  const [fetchCopyDischargeDocuments, setFetchCopyDischargeDocuments] = useState([]);
  const [loadingFetchCopyDocs, setLoadingFetchCopyDocs] = useState(false);

  /** Documents selected from header (Document Name) to be mapped on Save. Each: { doc, sectionKey }. */
  const [pendingDocumentsToMap, setPendingDocumentsToMap] = useState([]);

  /** IDs of documents initially loaded in first column (already saved). Save only sends NEW docs. */
  const initiallyLoadedDocIdsRef = useRef(new Set());

  /** Name+SubType keys of initially loaded docs (for matching when IDs differ between APIs). */
  const initiallyLoadedNameSubTypeKeysRef = useRef(new Set());

  /**
   * Fetch document names by document type. No pagination – fetches all pages and merges.
   * API: default-document-name-list-v2?status=ACTIVE&documentType=<selected>
   * Response: { docNameData: [], totalPages, total, hasMore }
   */
  const fetchDocumentNamesByType = async (documentType) => {
    if (!documentType || String(documentType).trim() === "") {
      setDocumentNameList([]);
      return;
    }
    setLoadingDocumentNames(true);
    try {
      const limit = 500;
      const first = await ProcedureFlowServices.getDefaultDocumentNameListV2({
        status: "ACTIVE",
        documentType: String(documentType).trim(),
        page: 1,
        limit,
      });
      const apiData = first?.data;
      const total = apiData?.total ?? 0;
      const totalPages =
        apiData?.totalPages ?? Math.max(1, Math.ceil(total / limit));
      let list = Array.isArray(apiData?.docNameData) ? apiData.docNameData : [];

      if (totalPages > 1) {
        const rest = await Promise.all(
          Array.from({ length: totalPages - 1 }, (_, i) =>
            ProcedureFlowServices.getDefaultDocumentNameListV2({
              status: "ACTIVE",
              documentType: String(documentType).trim(),
              page: i + 2,
              limit,
            })
          )
        );
        rest.forEach((res) => {
          const pageList = Array.isArray(res?.data?.docNameData)
            ? res.data.docNameData
            : [];
          list = list.concat(pageList);
        });
      }

      setDocumentNameList(list);
    } catch (err) {
      console.error("Error fetching document name list:", err);
      setDocumentNameList([]);
    } finally {
      setLoadingDocumentNames(false);
    }
  };

  /** Fetch documents for all document types and merge (when user selects "All"). */
  const fetchAllDocumentNames = async () => {
    const types = documentTypes
      .map((d) => (typeof d === "string" ? d : d.value))
      .filter((v) => v && String(v).trim() !== "" && v !== "All");
    if (types.length === 0) {
      setDocumentNameList([]);
      return;
    }
    setLoadingDocumentNames(true);
    try {
      const limit = 500;
      const fetchForType = async (documentType) => {
        let list = [];
        let page = 1;
        let hasMore = true;
        while (hasMore) {
          const res = await ProcedureFlowServices.getDefaultDocumentNameListV2({
            status: "ACTIVE",
            documentType: String(documentType).trim(),
            page,
            limit,
          });
          const apiData = res?.data;
          const pageList = Array.isArray(apiData?.docNameData) ? apiData.docNameData : [];
          list = list.concat(
            pageList.map((d) => ({ ...d, documentType: d.documentType ?? documentType })),
          );
          const total = apiData?.total ?? 0;
          const totalPages = apiData?.totalPages ?? Math.max(1, Math.ceil(total / limit));
          hasMore = page < totalPages && pageList.length >= limit;
          page += 1;
        }
        return list;
      };
      const results = await Promise.all(types.map((t) => fetchForType(t)));
      const merged = results.flat();
      const seen = new Set();
      const deduped = merged.filter((d) => {
        const id = d?.documentTypeMappingId ?? d?.id ?? d?.documentId ?? "";
        const key = id || `${d?.documentName ?? ""}|${d?.documentSubType ?? ""}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      setDocumentNameList(deduped);
    } catch (err) {
      console.error("Error fetching all document names:", err);
      setDocumentNameList([]);
    } finally {
      setLoadingDocumentNames(false);
    }
  };

  const fetchSchemes = async () => {
    if (schemes.length > 0) return;
    try {
      setLoadingSchemes(true);
      const limit = 500;
      const first = await ProcedureFlowServices.getDefaultSchemeTypes({
        page: 1,
        limit,
      });
      const apiData = first?.data;
      const total = apiData?.total ?? (Array.isArray(apiData?.data) ? apiData.data.length : 0);
      const totalPages =
        apiData?.totalPages ?? Math.max(1, Math.ceil(total / limit));
      let list = Array.isArray(apiData?.data) ? apiData.data : [];

      if (totalPages > 1) {
        const rest = await Promise.all(
          Array.from({ length: totalPages - 1 }, (_, i) =>
            ProcedureFlowServices.getDefaultSchemeTypes({
              page: i + 2,
              limit,
            })
          )
        );
        rest.forEach((res) => {
          const pageList = Array.isArray(res?.data?.data) ? res.data.data : [];
          list = list.concat(pageList);
        });
      }

      setSchemes(list);
    } catch (err) {
      console.error("Error fetching schemes:", err);
      setSchemes([]);
    } finally {
      setLoadingSchemes(false);
    }
  };

  const fetchDocumentType = async () => {
    try {
      setLoadingDocumentTypes(true);

      const response =
        await ProcedureFlowServices.getMasterDataList("documentType");

      const list = response?.data?.masterData?.allmasterdata || [];
      // console.log("the document tyoe is", list);
      setDocumentTypes(Array.isArray(list) ? list : []);
    } catch (error) {
      console.log("Error fetching document type", error);
      setDocumentTypes([]);
    } finally {
      setLoadingDocumentTypes(false);
    }
  };
  const fetchCaseType = async () => {
    try {
      setLoadingcaseType(true);

      const response =
        await ProcedureFlowServices.getMasterDataList("caseType");

      const list = response?.data?.masterData?.allmasterdata || [];
      // console.log("the case type is", list);
      setCaseType(Array.isArray(list) ? list : []);
    } catch (error) {
      console.log("Error fetching document type", error);
      setCaseType([]);
    } finally {
      setLoadingcaseType(false);
    }
  };
  const fetchSpecialties = async () => {
    if (specialties.length > 0) return;
    try {
      setLoadingSpecialties(true);
      const response = await ProcedureFlowServices.getSpecialityTypes({
        limit: 5000,
      });
      setSpecialties(
        Array.isArray(response?.data?.list) ? response.data.list : [],
      );
    } catch (err) {
      console.error("Error fetching specialties:", err);
      setSpecialties([]);
    } finally {
      setLoadingSpecialties(false);
    }
  };
  useEffect(() => {
    fetchSchemes();
    fetchDocumentType();
    fetchCaseType();
    fetchSpecialties();
  }, []);

  useEffect(() => {
    setDocumentNameList([]);
    setSelectedDocumentName("");
    if (!selectedDocumentType || String(selectedDocumentType).trim() === "") return;
    if (selectedDocumentType === "All") {
      fetchAllDocumentNames();
    } else {
      fetchDocumentNamesByType(selectedDocumentType);
    }
  }, [selectedDocumentType, documentTypes]);

  /** Fetch document names for Fetch and Copy block (independent of header). */
  const fetchFetchCopyDocumentNamesByType = async (documentType) => {
    if (!documentType || String(documentType).trim() === "") {
      setFetchCopyDocumentNameList([]);
      return;
    }
    setLoadingFetchCopyDocumentNames(true);
    try {
      const limit = 500;
      const first = await ProcedureFlowServices.getDefaultDocumentNameListV2({
        status: "ACTIVE",
        documentType: String(documentType).trim(),
        page: 1,
        limit,
      });
      const apiData = first?.data;
      const total = apiData?.total ?? 0;
      const totalPages =
        apiData?.totalPages ?? Math.max(1, Math.ceil(total / limit));
      let list = Array.isArray(apiData?.docNameData) ? apiData.docNameData : [];
      if (totalPages > 1) {
        const rest = await Promise.all(
          Array.from({ length: totalPages - 1 }, (_, i) =>
            ProcedureFlowServices.getDefaultDocumentNameListV2({
              status: "ACTIVE",
              documentType: String(documentType).trim(),
              page: i + 2,
              limit,
            })
          )
        );
        rest.forEach((res) => {
          const pageList = Array.isArray(res?.data?.docNameData)
            ? res.data.docNameData
            : [];
          list = list.concat(pageList);
        });
      }
      setFetchCopyDocumentNameList(list);
    } catch (err) {
      console.error("Error fetching fetch-copy document name list:", err);
      setFetchCopyDocumentNameList([]);
    } finally {
      setLoadingFetchCopyDocumentNames(false);
    }
  };

  useEffect(() => {
    setFetchCopyDocumentNameList([]);
    setFetchCopyDocumentName("");
    if (!fetchCopyDocumentType || String(fetchCopyDocumentType).trim() === "") return;

    let cancelled = false;
    const load = async () => {
      setLoadingFetchCopyDocumentNames(true);
      try {
        const types =
          fetchCopyDocumentType === "All"
            ? documentTypes
                .map((d) => (typeof d === "string" ? d : d.value))
                .filter((v) => v && String(v).trim() !== "" && v !== "All")
            : [String(fetchCopyDocumentType).trim()];

        if (types.length === 0 && fetchCopyDocumentType !== "All") {
          if (!cancelled) setFetchCopyDocumentNameList([]);
          return;
        }

        const fetchForType = async (documentType) => {
          const limit = 500;
          let list = [];
          let page = 1;
          let hasMore = true;
          while (hasMore) {
            const res = await ProcedureFlowServices.getDefaultDocumentNameListV2({
              status: "ACTIVE",
              documentType: String(documentType).trim(),
              page,
              limit,
            });
            const apiData = res?.data;
            const pageList = Array.isArray(apiData?.docNameData) ? apiData.docNameData : [];
            list = list.concat(
              pageList.map((d) => ({ ...d, documentType: d.documentType ?? documentType })),
            );
            const total = apiData?.total ?? 0;
            const totalPages = apiData?.totalPages ?? Math.max(1, Math.ceil(total / limit));
            hasMore = page < totalPages && pageList.length >= limit;
            page += 1;
          }
          return list;
        };

        const results = await Promise.all(types.map((t) => fetchForType(t)));
        const merged = results.flat();
        const seen = new Set();
        const deduped = merged.filter((d) => {
          const id = d?.documentTypeMappingId ?? d?.id ?? d?.documentId ?? "";
          const key = id || `${d?.documentName ?? ""}|${d?.documentSubType ?? ""}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        if (!cancelled) setFetchCopyDocumentNameList(deduped);
      } catch (err) {
        if (!cancelled) setFetchCopyDocumentNameList([]);
      } finally {
        if (!cancelled) setLoadingFetchCopyDocumentNames(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [fetchCopyDocumentType, documentTypes]);

  const documentTypeOptions = ["All", ...documentTypes.map((d) =>
    typeof d === "string" ? d : d.value,
  ).filter((v) => v && v !== "All")];

  /** Alias labels for document type values – shown in dropdown but actual values used in API calls */
  const documentTypeLabelMap = {
    All: "All",
    ADMISSIONDOCUMENT: "ADMISSION",
    DURINGTREATMENTDOCUMENT: "ENHANCEMENT",
    ONDISCHARGEDOCUMENT: "DISCHARGE",
  };

  const schemeOptions = schemes.map((s) =>
    typeof s === "string" ? s : s.name,
  );
  const caseTypeOptions = caseType.map((d) =>
    typeof d === "string" ? d : d.value,
  );
  const specialityTypeOptions = specialties.map((d) =>
    typeof d === "string" ? d : d.speciality,
  );

  /** Truncate specialty name to max 3 words + "..." for display. QA/senior: prevent lengthy names from breaking layout. */
  const truncateToThreeWords = (str) => {
    if (!str || typeof str !== "string") return str || "";
    const words = str.trim().split(/\s+/).filter(Boolean);
    if (words.length <= 3) return str;
    return words.slice(0, 3).join(" ") + "...";
  };

  /** Display label for specialty: prefix (code/id) : truncated name. e.g. "BM : Burns management" */
  const specialityLabelMap = {};
  specialties.forEach((d) => {
    const name = typeof d === "string" ? d : d.speciality;
    const prefix =
      typeof d === "object" && d?.specialityId != null
        ? String(d.specialityId)
        : "";
    const truncated = truncateToThreeWords(name);
    specialityLabelMap[name] = prefix ? `${prefix} : ${truncated}` : truncated;
  });
  const documentNameOptions = documentNameList.map((d) =>
    typeof d === "string" ? d : d.documentName ?? d.name ?? d.value ?? "",
  ).filter(Boolean);
  const fetchCopyDocumentNameOptions = fetchCopyDocumentNameList.map((d) =>
    typeof d === "string" ? d : d.documentName ?? d.name ?? d.value ?? "",
  ).filter(Boolean);

  const mergeDocumentsById = (existing, toAdd) => {
    const getId = (doc) =>
      String(
        (typeof doc === "object" && doc !== null
          ? doc.documentTypeMappingId ??
            doc.documenttypemapping?.documentId ??
            doc.documentId ??
            doc.id ??
            doc.documentName ??
            doc.name ??
            ""
          : doc) || "",
      );

    const existingIds = new Set(existing.map(getId).filter(Boolean));
    const result = [...existing];

    toAdd.forEach((doc) => {
      const id = getId(doc);
      if (!id || existingIds.has(id)) return;
      result.push(doc);
      existingIds.add(id);
    });

    return result;
  };

  const handleAddDocumentsToSection = async () => {
    if (!addDocumentSection || selectedDocumentsForAdd.length === 0) {
      toast.error("Please select at least one document to map.");
      return;
    }

    const toAdd = selectedDocumentsForAdd.filter(
      (doc) => !isDocAlreadyInSection(doc, addDocumentSection)
    );
    if (toAdd.length === 0) {
      toast.warn("All selected documents are already mapped in this section.");
      return;
    }
    if (toAdd.length < selectedDocumentsForAdd.length) {
      toast.info(`${selectedDocumentsForAdd.length - toAdd.length} document(s) skipped (already mapped).`);
    }

    const schemeMatch = schemes.find((s) => {
      const label = typeof s === "string" ? s : s.name;
      return label === selectedScheme;
    });

    const specialityMatch = specialties.find((s) => {
      const label = typeof s === "string" ? s : s.speciality;
      return label === selectedSpecialty;
    });

    const schemeTypeMasterId =
      schemeMatch && typeof schemeMatch === "object" && schemeMatch.id != null
        ? String(schemeMatch.id)
        : "";
    const specialityId =
      specialityMatch &&
      typeof specialityMatch === "object" &&
      specialityMatch.specialityId
        ? String(specialityMatch.specialityId)
        : "";
    const caseTypeValue = (selectedCaseType || "").trim();

    if (!schemeTypeMasterId || !specialityId || !caseTypeValue) {
      toast.error("Please select Scheme, Speciality and Case Type.");
      return;
    }

    const payload = toAdd
      .map((doc) => {
        const rawId =
          (doc && typeof doc === "object"
            ? doc.documentTypeMappingId ??
              doc.id ??
              doc.documenttypemapping?.id ??
              doc.documentId
            : null) ?? null;
        if (!rawId) return null;
        return {
          schemeTypeMasterId,
          specialityId,
          documentTypeMappingId: String(rawId),
          caseType: caseTypeValue,
        };
      })
      .filter(Boolean);

    if (payload.length === 0) {
      toast.error("Selected documents are missing required ids.");
      return;
    }

    setSavingMapping(true);
    try {
      await ProcedureFlowServices.updateDocTypeCaseTypeSpecialityMapping(
        payload,
      );
      toast.success("Documents mapped successfully.");

      const docsToAdd = toAdd;

      if (addDocumentSection === "admission") {
        setAdmissionDocuments((prev) => mergeDocumentsById(prev, docsToAdd));
      } else if (addDocumentSection === "enhancement") {
        setEnhancementDocuments((prev) => mergeDocumentsById(prev, docsToAdd));
      } else if (addDocumentSection === "discharge") {
        setDischargeDocuments((prev) => mergeDocumentsById(prev, docsToAdd));
      }

      setOpenAddDocumentModal(false);
      setAddDocumentSection(null);
      setSelectedDocumentsForAdd([]);
    } catch (error) {
      handleApiErrorResponses(error);
    } finally {
      setSavingMapping(false);
    }
  };

  /** Build payload from current header filters (Scheme, Speciality, Case Type, Document Name) and call mapping API */
  const handleSaveFromHeaderFilters = async () => {
    const schemeMatch = schemes.find((s) => {
      const label = typeof s === "string" ? s : s.name;
      return label === selectedScheme;
    });
    const specialityMatch = specialties.find((s) => {
      const label = typeof s === "string" ? s : s.speciality;
      return label === selectedSpecialty;
    });
    const docMatch = (documentNameList || []).find((d) => {
      const label =
        typeof d === "string" ? d : d?.documentName ?? d?.name ?? d?.value ?? "";
      return label === selectedDocumentName;
    });

    const schemeTypeMasterId =
      schemeMatch && typeof schemeMatch === "object" && schemeMatch.id != null
        ? String(schemeMatch.id)
        : "";
    const specialityId =
      specialityMatch &&
      typeof specialityMatch === "object" &&
      specialityMatch.specialityId
        ? String(specialityMatch.specialityId)
        : "";
    const caseTypeValue = (selectedCaseType || "").trim();
    const documentTypeMappingId =
      docMatch && typeof docMatch === "object"
        ? String(
            docMatch.documentTypeMappingId ??
              docMatch.id ??
              docMatch.documenttypemapping?.id ??
              docMatch.documentId ??
              "",
          )
        : "";

    if (!schemeTypeMasterId || !specialityId || !caseTypeValue) {
      toast.error("Please select Scheme, Speciality and Case Type.");
      return;
    }
    if (!documentTypeMappingId || !selectedDocumentName) {
      toast.error("Please select a Document Name to map.");
      return;
    }

    const payload = [
      {
        schemeTypeMasterId,
        specialityId,
        documentTypeMappingId,
        caseType: caseTypeValue,
      },
    ];

    setSavingFromHeader(true);
    try {
      await ProcedureFlowServices.updateDocTypeCaseTypeSpecialityMapping(
        payload,
      );
      toast.success("Document mapped successfully.");
    } catch (error) {
      handleApiErrorResponses(error);
    } finally {
      setSavingFromHeader(false);
    }
  };

  /** Save all documents in the first column (Admission, Enhancement, Discharge/Claim) using header filter. */
  /** Use documentTypeMappingId from API response so save payload maps docs correctly (same as Add document flow). */
  const getDocMappingId = (doc) =>
    String(
      (doc && typeof doc === "object"
        ? doc.documentTypeMappingId ??
          doc.id ??
          doc.documenttypemapping?.documentTypeMappingId ??
          doc.documenttypemapping?.id ??
          doc.documentId ??
          ""
        : "") || "",
    );

  const getDocNameSubTypeKey = (doc) => {
    if (!doc || typeof doc !== "object") return "";
    const name = doc.documenttypemapping?.documentName ?? doc.documentName ?? doc.name ?? "";
    const subType = doc.documenttypemapping?.documentSubType ?? doc.documentSubType ?? doc.documentSubtype ?? "";
    return `${String(name).trim()}|${String(subType).trim()}`;
  };

  const handleSaveAllSections = async () => {
    const schemeMatch = schemes.find((s) => {
      const label = typeof s === "string" ? s : s.name;
      return label === selectedScheme;
    });
    const specialityMatch = specialties.find((s) => {
      const label = typeof s === "string" ? s : s.speciality;
      return label === selectedSpecialty;
    });
    const schemeTypeMasterId =
      schemeMatch && typeof schemeMatch === "object" && schemeMatch.id != null
        ? String(schemeMatch.id)
        : "";
    const specialityId =
      specialityMatch &&
      typeof specialityMatch === "object" &&
      specialityMatch.specialityId
        ? String(specialityMatch.specialityId)
        : "";
    const caseTypeValue = (selectedCaseType || "").trim();

    if (!schemeTypeMasterId || !specialityId || !caseTypeValue) {
      toast.error("Please select Scheme, Speciality and Case Type in the header filters.");
      return;
    }

    /* Include pending "selected to map" documents from header in the save */
    const pendingAdmission = (pendingDocumentsToMap || []).filter((p) => p.sectionKey === "admission").map((p) => p.doc);
    const pendingEnhancement = (pendingDocumentsToMap || []).filter((p) => p.sectionKey === "enhancement").map((p) => p.doc);
    const pendingDischarge = (pendingDocumentsToMap || []).filter((p) => p.sectionKey === "discharge").map((p) => p.doc);
    const allDocs = [
      ...(admissionDocuments || []),
      ...(enhancementDocuments || []),
      ...(dischargeDocuments || []),
      ...pendingAdmission,
      ...pendingEnhancement,
      ...pendingDischarge,
    ];
    /* Deduplicate by documentTypeMappingId so we don't save the same document twice */
    const seenIds = new Set();
    const uniqueDocs = allDocs.filter((doc) => {
      const id = getDocMappingId(doc);
      if (!id || seenIds.has(id)) return false;
      seenIds.add(id);
      return true;
    });
    /* Only save NEW documents – skip ones already loaded (avoid re-saving existing mappings) */
    const initialIds = initiallyLoadedDocIdsRef.current;
    const initialNameSubTypeKeys = initiallyLoadedNameSubTypeKeysRef.current;
    const newDocsOnly = uniqueDocs.filter((doc) => {
      const id = getDocMappingId(doc);
      const key = getDocNameSubTypeKey(doc);
      if (id && initialIds.has(id)) return false;
      if (key && initialNameSubTypeKeys.has(key)) return false;
      return true;
    });
    const payload = newDocsOnly
      .map((doc) => {
        const id = getDocMappingId(doc);
        if (!id) return null;
        return {
          schemeTypeMasterId,
          specialityId,
          documentTypeMappingId: id,
          caseType: caseTypeValue,
        };
      })
      .filter(Boolean);

    if (payload.length === 0) {
      toast.error("No new documents to save. All documents are already mapped.");
      return;
    }

    setSavingFromHeader(true);
    try {
      await ProcedureFlowServices.updateDocTypeCaseTypeSpecialityMapping(payload);
      /* Merge pending documents into section state and clear pending */
      setAdmissionDocuments((prev) => mergeDocumentsById(prev, pendingAdmission));
      setEnhancementDocuments((prev) => mergeDocumentsById(prev, pendingEnhancement));
      setDischargeDocuments((prev) => mergeDocumentsById(prev, pendingDischarge));
      setPendingDocumentsToMap([]);
      toast.success("Default document mapping saved successfully.");
      /* Refresh first column to show latest document list */
      await fetchMappedDocsByFilters(true);
    } catch (error) {
      handleApiErrorResponses(error);
    } finally {
      setSavingFromHeader(false);
    }
  };

  /**
   * Map header Document Type value to section key (admission / enhancement / discharge).
   * API uses ADMISSIONDOCUMENT, DURINGTREATMENTDOCUMENT, ONDISCHARGEDOCUMENT.
   */
  const getSectionKeyFromDocumentType = (documentType) => {
    if (!documentType || typeof documentType !== "string") return null;
    const t = documentType.toUpperCase();
    if (t === "ADMISSIONDOCUMENT") return "admission";
    if (t === "DURINGTREATMENTDOCUMENT") return "enhancement";
    if (t === "ONDISCHARGEDOCUMENT") return "discharge";
    return null;
  };

  /**
   * Determine if a section should be visible based on selected Document Type.
   * If no document type is selected, all sections are visible.
   * Otherwise, only the matching section is visible.
   */
  const shouldShowSection = (sectionKey) => {
    if (!selectedDocumentType) return true; // Show all if no filter selected
    const selectedSection = getSectionKeyFromDocumentType(selectedDocumentType);
    return selectedSection === sectionKey;
  };

  /** Check if a doc is already in the given section (admission/enhancement/discharge). */
  const isDocAlreadyInSection = (doc, sectionKey) => {
    const getId = (d) =>
      String(
        (d && typeof d === "object"
          ? d.documentTypeMappingId ?? d.id ?? d.documenttypemapping?.id ?? d.documentId ?? ""
          : "") || "",
      );
    const getLabel = (d) =>
      (d && typeof d === "object"
        ? d.documentName ?? d.name ?? d.documenttypemapping?.documentName ?? ""
        : "") || "";
    const getSubType = (d) =>
      (d && typeof d === "object"
        ? d.documentSubType ?? d.documenttypemapping?.documentSubType ?? ""
        : "") || "";
    const docId = getId(doc);
    const docName = getLabel(doc);
    const docSub = getSubType(doc);
    const section =
      sectionKey === "admission"
        ? admissionDocuments
        : sectionKey === "enhancement"
        ? enhancementDocuments
        : dischargeDocuments;
    return (section || []).some((d) => {
      if (docId && getId(d) === docId) return true;
      if (docName && getLabel(d) === docName && getSubType(d) === docSub) return true;
      return false;
    });
  };

  /** When user selects a document from header Document Name dropdown, add it to the "selected to map" list below. */
  const handleDocumentNameSelect = (value) => {
    setSelectedDocumentName(value);
    if (!value || !selectedDocumentType) return;
    const docMatch = (documentNameList || []).find((d) => {
      const label =
        typeof d === "string" ? d : d?.documentName ?? d?.name ?? d?.value ?? "";
      return label === value;
    });
    if (!docMatch) return;
    const docType =
      selectedDocumentType === "All"
        ? docMatch?.documentType ?? docMatch?.documenttypemapping?.documentType ?? ""
        : selectedDocumentType;
    const sectionKey = getSectionKeyFromDocumentType(docType);
    if (!sectionKey) return;
    const id = String(docMatch.documentId ?? docMatch.id ?? docMatch.documentTypeMappingId ?? "");
    if (!id) return;
    if (isDocAlreadyInSection(docMatch, sectionKey)) {
      toast.warn("This document is already mapped in this section.");
      setSelectedDocumentName("");
      return;
    }
    setPendingDocumentsToMap((prev) => {
      if (prev.some((p) => (String(p.doc.documentId ?? p.doc.id ?? p.doc.documentTypeMappingId) === id) && p.sectionKey === sectionKey))
        return prev;
      return [...prev, { doc: docMatch, sectionKey }];
    });
    setSelectedDocumentName(""); // reset so user can select another document
  };

  /** Remove a document from the pending "selected to map" list. */
  const removePendingDocument = (index) => {
    setPendingDocumentsToMap((prev) => prev.filter((_, i) => i !== index));
  };

  /** Copy a document from second column into the matching first-column section (one by one). */
  const handleCopyFromFetchCopy = (sectionKey, doc) => {
    if (sectionKey === "admission") {
      setAdmissionDocuments((prev) => mergeDocumentsById(prev, [doc]));
      toast.success("Document copied to Admission.");
    } else if (sectionKey === "enhancement") {
      setEnhancementDocuments((prev) => mergeDocumentsById(prev, [doc]));
      toast.success("Document copied to Enhancement.");
    } else if (sectionKey === "discharge") {
      setDischargeDocuments((prev) => mergeDocumentsById(prev, [doc]));
      toast.success("Document copied to Discharge / Claim.");
    }
  };

  /** Merge all documents from one section (right column) into the matching left-column section. */
  const handleMergeSection = (sectionKey) => {
    if (sectionKey === "admission") {
      if (!Array.isArray(fetchCopyAdmissionDocuments) || fetchCopyAdmissionDocuments.length === 0) {
        toast.warning("No Admission documents to merge.");
        return;
      }
      setAdmissionDocuments((prev) => mergeDocumentsById(prev, fetchCopyAdmissionDocuments));
      toast.success("All Admission documents merged.");
    } else if (sectionKey === "enhancement") {
      if (!Array.isArray(fetchCopyEnhancementDocuments) || fetchCopyEnhancementDocuments.length === 0) {
        toast.warning("No Enhancement documents to merge.");
        return;
      }
      setEnhancementDocuments((prev) => mergeDocumentsById(prev, fetchCopyEnhancementDocuments));
      toast.success("All Enhancement documents merged.");
    } else if (sectionKey === "discharge") {
      if (!Array.isArray(fetchCopyDischargeDocuments) || fetchCopyDischargeDocuments.length === 0) {
        toast.warning("No Discharge / Claim documents to merge.");
        return;
      }
      setDischargeDocuments((prev) => mergeDocumentsById(prev, fetchCopyDischargeDocuments));
      toast.success("All Discharge / Claim documents merged.");
    }
  };

  /** Merge all Admission, Enhancement and Discharge/Claim documents from right column into left at once. */
  const handleMergeAll = () => {
    const hasAdmission = Array.isArray(fetchCopyAdmissionDocuments) && fetchCopyAdmissionDocuments.length > 0;
    const hasEnhancement = Array.isArray(fetchCopyEnhancementDocuments) && fetchCopyEnhancementDocuments.length > 0;
    const hasDischarge = Array.isArray(fetchCopyDischargeDocuments) && fetchCopyDischargeDocuments.length > 0;
    
    if (!hasAdmission && !hasEnhancement && !hasDischarge) {
      toast.warning("No documents to merge.");
      return;
    }
    
    if (hasAdmission) {
      setAdmissionDocuments((prev) => mergeDocumentsById(prev, fetchCopyAdmissionDocuments));
    }
    if (hasEnhancement) {
      setEnhancementDocuments((prev) => mergeDocumentsById(prev, fetchCopyEnhancementDocuments));
    }
    if (hasDischarge) {
      setDischargeDocuments((prev) => mergeDocumentsById(prev, fetchCopyDischargeDocuments));
    }
    
    toast.success("All documents merged (Admission, Enhancement, Discharge / Claim).");
  };

  /** Replace left column entirely with right column data (second filter). */
  const handleReplaceAll = () => {
    const hasAdmission = Array.isArray(fetchCopyAdmissionDocuments) && fetchCopyAdmissionDocuments.length > 0;
    const hasEnhancement = Array.isArray(fetchCopyEnhancementDocuments) && fetchCopyEnhancementDocuments.length > 0;
    const hasDischarge = Array.isArray(fetchCopyDischargeDocuments) && fetchCopyDischargeDocuments.length > 0;
    
    if (!hasAdmission && !hasEnhancement && !hasDischarge) {
      toast.warning("No documents to replace with.");
      return;
    }
    
    setAdmissionDocuments([...(fetchCopyAdmissionDocuments || [])]);
    setEnhancementDocuments([...(fetchCopyEnhancementDocuments || [])]);
    setDischargeDocuments([...(fetchCopyDischargeDocuments || [])]);
    toast.success("Default Mapped Documents replaced with fetched documents.");
  };

  /**
   * Delete/remove mapping: same API as save (update-doctype-casetype-speciality-mapping)
   * but payload is [{ id: "<uuid-from-fetched-doc>", status: "INACTIVE" }].
   * The id is the record id from default-ctype-speciality-docs response (e.g. "64874ef0-d329-4c13-8704-10b5f604fe58").
   */
  const handleConfirmRemoveMapping = async () => {
    if (!Array.isArray(selectedToRemove) || selectedToRemove.length === 0) {
      setOpenConfirmModal(false);
      return;
    }

    const docsToRemove = [];
    selectedToRemove.forEach(({ type, index }) => {
      const list =
        type === "admission"
          ? admissionDocuments
          : type === "enhancement"
            ? enhancementDocuments
            : dischargeDocuments;
      const doc = Array.isArray(list) && list[index];
      if (doc) docsToRemove.push({ type, index, doc });
    });

    /* Only docs from default-ctype-speciality-docs have the record id (UUID) needed for delete API */
    const withRecordId = docsToRemove.filter(({ doc }) => doc && doc.id);
    const withoutRecordId = docsToRemove.filter(({ doc }) => !doc || !doc.id);

    setRemovingMapping(true);
    try {
      if (withRecordId.length > 0) {
        const deletePayload = withRecordId.map(({ doc }) => ({
          id: String(doc.id),
          status: "INACTIVE",
        }));
        await ProcedureFlowServices.updateDocTypeCaseTypeSpecialityMapping(deletePayload);
        toast.success("Mapping removed successfully.");
      }
      if (withoutRecordId.length > 0) {
        toast.info("Some items were removed from the list only (not saved yet).");
      }
      const indicesByType = { admission: new Set(), enhancement: new Set(), discharge: new Set() };
      selectedToRemove.forEach(({ type, index }) => {
        if (indicesByType[type]) indicesByType[type].add(index);
      });
      setAdmissionDocuments((prev) =>
        prev.filter((_, i) => !indicesByType.admission.has(i)),
      );
      setEnhancementDocuments((prev) =>
        prev.filter((_, i) => !indicesByType.enhancement.has(i)),
      );
      setDischargeDocuments((prev) =>
        prev.filter((_, i) => !indicesByType.discharge.has(i)),
      );
      setOpenConfirmModal(false);
      setSelectedToRemove([]);
    } catch (error) {
      handleApiErrorResponses(error);
      toast.error("Failed to remove mapping.");
    } finally {
      setRemovingMapping(false);
    }
  };

  /** Normalize API response to array of items. default-ctype-speciality-docs returns { defaultCaseDocuments: [...] } with documentName in documenttypemapping. */
  const normalizeDocList = (data) => {
    if (!data || typeof data !== "object") return [];
    if (Array.isArray(data)) return data;
    const keys = [
      "defaultCaseDocuments",
      "data",
      "list",
      "docNameData",
      "documents",
      "result",
      "items",
      "content",
      "mappedDocuments",
      "defaultDocuments",
    ];
    for (const key of keys) {
      if (Array.isArray(data[key])) return data[key];
    }
    return [];
  };

  /** Filter list to items matching the given document type (item.documentType or item.documenttypemapping?.documentType). */
  const filterByDocumentType = (list, documentType) => {
    if (!Array.isArray(list) || !documentType) return [];
    const typeUpper = String(documentType).toUpperCase();
    return list.filter((item) => {
      const itemType =
        item?.documentType ?? item?.documenttypemapping?.documentType ?? "";
      return String(itemType).toUpperCase() === typeUpper;
    });
  };

  /**
   * Fetch admission, enhancement, discharge by current header filters (Scheme, Speciality, Case Type).
   * Used automatically when filters are selected; optional silentSuccess to avoid toast on auto-load.
   */
  const fetchMappedDocsByFilters = async (silentSuccess = false) => {
    const schemeMatch = schemes.find((s) => {
      const label = typeof s === "string" ? s : s.name;
      return label === selectedScheme;
    });
    const specialityMatch = specialties.find((s) => {
      const label = typeof s === "string" ? s : s.speciality;
      return label === selectedSpecialty;
    });

    const schemeTypeMasterId =
      schemeMatch && typeof schemeMatch === "object" && schemeMatch.id != null
        ? String(schemeMatch.id)
        : "";
    const specialityId =
      specialityMatch &&
      typeof specialityMatch === "object" &&
      specialityMatch.specialityId
        ? String(specialityMatch.specialityId)
        : "";
    const caseTypeValue = (selectedCaseType || "").trim();

    if (!schemeTypeMasterId || !specialityId || !caseTypeValue) {
      return;
    }

    setLoadingFetchedDocs(true);
    try {
      const [admissionRes, enhancementRes, dischargeRes] = await Promise.all([
        ProcedureFlowServices.getDefaultCtypeSpecialityDocs({
          caseType: caseTypeValue,
          schemeTypeMasterId,
          specialityId,
          documentType: "ADMISSIONDOCUMENT",
        }),
        ProcedureFlowServices.getDefaultCtypeSpecialityDocs({
          caseType: caseTypeValue,
          schemeTypeMasterId,
          specialityId,
          documentType: "DURINGTREATMENTDOCUMENT",
        }),
        ProcedureFlowServices.getDefaultCtypeSpecialityDocs({
          caseType: caseTypeValue,
          schemeTypeMasterId,
          specialityId,
          documentType: "ONDISCHARGEDOCUMENT",
        }),
      ]);

      const admissionList = filterByDocumentType(
        normalizeDocList(admissionRes?.data),
        "ADMISSIONDOCUMENT",
      );
      const enhancementList = filterByDocumentType(
        normalizeDocList(enhancementRes?.data),
        "DURINGTREATMENTDOCUMENT",
      );
      const dischargeList = filterByDocumentType(
        normalizeDocList(dischargeRes?.data),
        "ONDISCHARGEDOCUMENT",
      );

      setAdmissionDocuments(admissionList);
      setEnhancementDocuments(enhancementList);
      setDischargeDocuments(dischargeList);
      const allIds = new Set();
      const nameSubTypeKeys = new Set();
      const getNameSubTypeKey = (d) => {
        const name = d?.documenttypemapping?.documentName ?? d?.documentName ?? d?.name ?? "";
        const subType = d?.documenttypemapping?.documentSubType ?? d?.documentSubType ?? d?.documentSubtype ?? "";
        return `${String(name).trim()}|${String(subType).trim()}`;
      };
      [admissionList, enhancementList, dischargeList].forEach((list) => {
        (list || []).forEach((d) => {
          const id = d?.documentTypeMappingId ?? d?.id ?? d?.documenttypemapping?.id ?? d?.documentId ?? "";
          if (id) allIds.add(String(id));
          const key = getNameSubTypeKey(d);
          if (key) nameSubTypeKeys.add(key);
        });
      });
      initiallyLoadedDocIdsRef.current = allIds;
      initiallyLoadedNameSubTypeKeysRef.current = nameSubTypeKeys;
      if (!silentSuccess) toast.success("Fetched mapped documents.");
    } catch (error) {
      handleApiErrorResponses(error);
      toast.error("Failed to fetch mapped documents.");
    } finally {
      setLoadingFetchedDocs(false);
    }
  };

  /** Auto-load documents when Scheme, Speciality and Case Type are selected (no Fetch button). */
  useEffect(() => {
    const schemeMatch = schemes.find((s) => {
      const label = typeof s === "string" ? s : s.name;
      return label === selectedScheme;
    });
    const specialityMatch = specialties.find((s) => {
      const label = typeof s === "string" ? s : s.speciality;
      return label === selectedSpecialty;
    });
    const schemeTypeMasterId =
      schemeMatch && typeof schemeMatch === "object" && schemeMatch.id != null
        ? String(schemeMatch.id)
        : "";
    const specialityId =
      specialityMatch &&
      typeof specialityMatch === "object" &&
      specialityMatch.specialityId
        ? String(specialityMatch.specialityId)
        : "";
    const caseTypeValue = (selectedCaseType || "").trim();
    const canFetch =
      schemeTypeMasterId &&
      specialityId &&
      caseTypeValue &&
      schemes.length > 0 &&
      specialties.length > 0;

    if (!canFetch) {
      setAdmissionDocuments([]);
      setEnhancementDocuments([]);
      setDischargeDocuments([]);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoadingFetchedDocs(true);
      try {
        const [admissionRes, enhancementRes, dischargeRes] = await Promise.all([
          ProcedureFlowServices.getDefaultCtypeSpecialityDocs({
            caseType: caseTypeValue,
            schemeTypeMasterId,
            specialityId,
            documentType: "ADMISSIONDOCUMENT",
          }),
          ProcedureFlowServices.getDefaultCtypeSpecialityDocs({
            caseType: caseTypeValue,
            schemeTypeMasterId,
            specialityId,
            documentType: "DURINGTREATMENTDOCUMENT",
          }),
          ProcedureFlowServices.getDefaultCtypeSpecialityDocs({
            caseType: caseTypeValue,
            schemeTypeMasterId,
            specialityId,
            documentType: "ONDISCHARGEDOCUMENT",
          }),
        ]);
        if (cancelled) return;
        const admissionList = filterByDocumentType(
          normalizeDocList(admissionRes?.data),
          "ADMISSIONDOCUMENT",
        );
        const enhancementList = filterByDocumentType(
          normalizeDocList(enhancementRes?.data),
          "DURINGTREATMENTDOCUMENT",
        );
        const dischargeList = filterByDocumentType(
          normalizeDocList(dischargeRes?.data),
          "ONDISCHARGEDOCUMENT",
        );
        setAdmissionDocuments(admissionList);
        setEnhancementDocuments(enhancementList);
        setDischargeDocuments(dischargeList);
        const allIds = new Set();
        const nameSubTypeKeys = new Set();
        const getNameSubTypeKey = (d) => {
          const name = d?.documenttypemapping?.documentName ?? d?.documentName ?? d?.name ?? "";
          const subType = d?.documenttypemapping?.documentSubType ?? d?.documentSubType ?? d?.documentSubtype ?? "";
          return `${String(name).trim()}|${String(subType).trim()}`;
        };
        [admissionList, enhancementList, dischargeList].forEach((list) => {
          (list || []).forEach((d) => {
            const id = d?.documentTypeMappingId ?? d?.id ?? d?.documenttypemapping?.id ?? d?.documentId ?? "";
            if (id) allIds.add(String(id));
            const key = getNameSubTypeKey(d);
            if (key) nameSubTypeKeys.add(key);
          });
        });
        initiallyLoadedDocIdsRef.current = allIds;
        initiallyLoadedNameSubTypeKeysRef.current = nameSubTypeKeys;
      } catch (error) {
        if (!cancelled) {
          handleApiErrorResponses(error);
          toast.error("Failed to load mapped documents.");
        }
      } finally {
        if (!cancelled) setLoadingFetchedDocs(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [
    selectedScheme,
    selectedSpecialty,
    selectedCaseType,
    schemes,
    specialties,
  ]);

  /** Fetch documents using second filter (Fetch and Copy) and switch to two-column layout. */
  const fetchFetchCopyMappedDocs = async () => {
    const schemeMatch = schemes.find((s) => {
      const label = typeof s === "string" ? s : s.name;
      return label === fetchCopyScheme;
    });
    const specialityMatch = specialties.find((s) => {
      const label = typeof s === "string" ? s : s.speciality;
      return label === fetchCopySpecialty;
    });
    const schemeTypeMasterId =
      schemeMatch && typeof schemeMatch === "object" && schemeMatch.id != null
        ? String(schemeMatch.id)
        : "";
    const specialityId =
      specialityMatch &&
      typeof specialityMatch === "object" &&
      specialityMatch.specialityId
        ? String(specialityMatch.specialityId)
        : "";
    const caseTypeValue = (fetchCopyCaseType || "").trim();

    if (!schemeTypeMasterId || !specialityId || !caseTypeValue) {
      toast.error("Please select Scheme, Speciality and Case Type in the Fetch and Copy filters.");
      return;
    }

    setLoadingFetchCopyDocs(true);
    try {
      const [admissionRes, enhancementRes, dischargeRes] = await Promise.all([
        ProcedureFlowServices.getDefaultCtypeSpecialityDocs({
          caseType: caseTypeValue,
          schemeTypeMasterId,
          specialityId,
          documentType: "ADMISSIONDOCUMENT",
        }),
        ProcedureFlowServices.getDefaultCtypeSpecialityDocs({
          caseType: caseTypeValue,
          schemeTypeMasterId,
          specialityId,
          documentType: "DURINGTREATMENTDOCUMENT",
        }),
        ProcedureFlowServices.getDefaultCtypeSpecialityDocs({
          caseType: caseTypeValue,
          schemeTypeMasterId,
          specialityId,
          documentType: "ONDISCHARGEDOCUMENT",
        }),
      ]);

      const admissionList = filterByDocumentType(
        normalizeDocList(admissionRes?.data),
        "ADMISSIONDOCUMENT",
      );
      const enhancementList = filterByDocumentType(
        normalizeDocList(enhancementRes?.data),
        "DURINGTREATMENTDOCUMENT",
      );
      const dischargeList = filterByDocumentType(
        normalizeDocList(dischargeRes?.data),
        "ONDISCHARGEDOCUMENT",
      );

      setFetchCopyAdmissionDocuments(admissionList);
      setFetchCopyEnhancementDocuments(enhancementList);
      setFetchCopyDischargeDocuments(dischargeList);
      setShowTwoColumnLayout(true);
      toast.success("Fetched documents. You can copy to Default Mapped Documents.");
    } catch (error) {
      handleApiErrorResponses(error);
      toast.error("Failed to fetch documents from existing.");
    } finally {
      setLoadingFetchCopyDocs(false);
    }
  };

  /**
   * Reset all filters and clear data
   */
  const handleReset = () => {
    // Reset primary filters
    setSelectedScheme("");
    setSelectedSpecialty(null);
    setSelectedDocumentType("");
    setSelectedDocumentName("");
    setSelectedCaseType("");
    setDocumentNameList([]);

    // Reset fetch and copy filters
    setFetchCopyScheme("");
    setFetchCopySpecialty(null);
    setFetchCopyDocumentType("");
    setFetchCopyDocumentName("");
    setFetchCopyCaseType("");
    setFetchCopyDocumentNameList([]);

    // Clear document lists
    setAdmissionDocuments([]);
    setEnhancementDocuments([]);
    setDischargeDocuments([]);
    setFetchCopyAdmissionDocuments([]);
    setFetchCopyEnhancementDocuments([]);
    setFetchCopyDischargeDocuments([]);

    // Clear pending mappings
    setPendingDocumentsToMap([]);
    setSelectedDocumentsForAdd([]);

    // Reset layout
    setShowTwoColumnLayout(false);
    setMode("FILTER");

    toast.success("All filters and data have been reset.");
  };

  return (
    <div className="w-full bg-[#F3FAF7] rounded-2xl p-8  min-h-screen min-h-[100dvh]">
      {/* ================= HEADER ================= */}
      <div className="flex items-center justify-between mt-14 mb-8">
        <h1 className="font-lufga font-normal text-[40px] tracking-[-0.04em] whitespace-normal">
          Default Document Mapping
        </h1>

        <button 
          onClick={handleReset}
          className="rounded-[54px] px-[36px] py-[14px] bg-[#7AD3AE] font-lufga font-medium text-[15px] border border-solid border-[#5AC69F] hover:bg-[#5AC69F] transition-colors cursor-pointer"
        >
          Reset
        </button>
      </div>

      {/* ================= FILTERS (SCREEN 1) ================= */}
      <div className="flex flex-wrap gap-6 mb-10">
        {/* <SelectField label="Scheme" onSelect={() => setMode("VIEW")} />
        <SelectField label="Speciality" onSelect={() => setMode("VIEW")} />
        <SelectField label="Document Type" onSelect={() => setMode("VIEW")} />
        <SelectField label="Case Type" onSelect={() => setMode("VIEW")} /> */}
        <FilterDropdown
          placeholder={"Scheme"}
          options={schemeOptions}
          disabled={loadingSchemes}
          value={selectedScheme}
          setValue={setSelectedScheme}
          enableSearch={true}
        />
        <FilterDropdown
          placeholder={"Speciality"}
          options={specialityTypeOptions}
          value={selectedSpecialty}
          setValue={setSelectedSpecialty}
          clearValue={null}
          enableSearch={true}
          labelMap={specialityLabelMap}
          valueTitle={selectedSpecialty}
        />
        <FilterDropdown
          placeholder="Document Type"
          options={documentTypeOptions}
          disabled={loadingDocumentTypes}
          value={selectedDocumentType}
          setValue={setSelectedDocumentType}
          labelMap={documentTypeLabelMap}
        />
        <FilterDropdown
          placeholder={
            !selectedDocumentType
              ? "Select document type first"
              : loadingDocumentNames
                ? "Loading..."
                : "Document Name"
          }
          options={documentNameOptions}
          disabled={!selectedDocumentType || loadingDocumentNames}
          value={selectedDocumentName}
          setValue={handleDocumentNameSelect}
          enableSearch={true}
          keepOpenOnSelect={true}
        />

        <FilterDropdown
          placeholder={"Case Type"}
          options={caseTypeOptions}
          value={selectedCaseType}
          setValue={setSelectedCaseType}
          disabled={loadingcaseType}
        />
      </div>

      {/* Selected from header: sticky section with clear all button */}
      {pendingDocumentsToMap.length > 0 && (
        <div className="sticky top-0 z-40 border border-[#E8E8E8] rounded-2xl bg-white px-4 py-3 mb-6 shadow-md">
          <div className="flex items-center justify-between mb-2">
            <div className="font-lufga text-[12px] font-medium text-gray-500">
              Selected ({pendingDocumentsToMap.length})
            </div>
            <button
              type="button"
              onClick={() => setPendingDocumentsToMap([])}
              className="px-3 py-1 rounded-full bg-red-50 border border-red-200 text-red-600 font-lufga text-[12px] font-medium hover:bg-red-100 transition-colors cursor-pointer"
              title="Clear all selected documents"
            >
              Clear All
            </button>
          </div>
          <div className="flex flex-wrap gap-2 max-h-[200px] overflow-y-auto">
            {pendingDocumentsToMap.map((item, index) => {
              const label =
                typeof item.doc === "string"
                  ? item.doc
                  : item.doc?.documentName ?? item.doc?.name ?? item.doc?.value ?? "—";
              return (
                <span
                  key={`${item.doc?.documentId ?? item.doc?.id ?? index}-${index}`}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#F4FBF8] border border-[#CDEFE2] rounded-2xl font-lufga text-[13px] text-[#1F6B4B] max-w-full min-w-0"
                >
                  <span className="break-words text-left flex-1 min-w-0">{label}</span>
                  <button
                    type="button"
                    onClick={() => removePendingDocument(index)}
                    className="cursor-pointer flex-shrink-0 text-gray-500 hover:text-black font-lufga text-[16px] leading-none"
                    title="Unselect"
                    aria-label="Unselect"
                  >
                    ×
                  </button>
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* ================= DEFAULT MAPPED DOCUMENTS (single column when not in two-column mode) ================= */}
      {loadingFetchedDocs && (
        <p className="font-lufga text-[14px] text-gray-500 mb-4">Loading documents…</p>
      )}
      {!showTwoColumnLayout && (
        <>
          <h2 className="font-lufga font-medium text-[28px] mb-8">
            Default Mapped Documents
          </h2>

          {shouldShowSection("admission") && (
            <MappedSection
              title="ADMISSION"
              documents={admissionDocuments}
              onAdd={() => {
                setAddDocumentSection("admission");
                setSelectedDocumentsForAdd([]);
                setOpenAddDocumentModal(true);
              }}
              onDelete={(index) => {
                setSelectedToRemove([{ type: "admission", index }]);
                setOpenConfirmModal(true);
              }}
            />
          )}

          {shouldShowSection("enhancement") && (
            <MappedSection
              title="ENHANCEMENT"
              documents={enhancementDocuments}
              onAdd={() => {
                setAddDocumentSection("enhancement");
                setSelectedDocumentsForAdd([]);
                setOpenAddDocumentModal(true);
              }}
              onDelete={(index) => {
                setSelectedToRemove([{ type: "enhancement", index }]);
                setOpenConfirmModal(true);
              }}
            />
          )}

          {shouldShowSection("discharge") && (
            <MappedSection
              title="DISCHARGE / CLAIM"
              documents={dischargeDocuments}
              onAdd={() => {
                setAddDocumentSection("discharge");
                setSelectedDocumentsForAdd([]);
                setOpenAddDocumentModal(true);
              }}
              onDelete={(index) => {
                setSelectedToRemove([{ type: "discharge", index }]);
                setOpenConfirmModal(true);
              }}
            />
          )}
        </>
      )}

      {/* ================= FETCH AND COPY DOCUMENTS – independent filters ================= */}
      <div className="bg-white rounded-[32px] p-6 flex flex-wrap items-center gap-6 mb-10 mt-10">
        <span className="font-lufga text-[15px] text-gray-700">
          Fetch and Copy Documents from existing :
        </span>
        <FilterDropdown
          placeholder="Scheme"
          options={schemeOptions}
          disabled={loadingSchemes}
          value={fetchCopyScheme}
          setValue={setFetchCopyScheme}
          enableSearch={true}
        />
        <FilterDropdown
          placeholder="Speciality"
          options={specialityTypeOptions}
          value={fetchCopySpecialty}
          setValue={setFetchCopySpecialty}
          clearValue={null}
          enableSearch={true}
          labelMap={specialityLabelMap}
          valueTitle={fetchCopySpecialty}
        />
        <FilterDropdown
          placeholder="Document Type"
          options={documentTypeOptions}
          disabled={loadingDocumentTypes}
          value={fetchCopyDocumentType}
          setValue={setFetchCopyDocumentType}
          labelMap={documentTypeLabelMap}
        />
        <FilterDropdown
          placeholder={
            !fetchCopyDocumentType
              ? "Select document type first"
              : loadingFetchCopyDocumentNames
                ? "Loading..."
                : "Document Name"
          }
          options={fetchCopyDocumentNameOptions}
          disabled={!fetchCopyDocumentType || loadingFetchCopyDocumentNames}
          value={fetchCopyDocumentName}
          setValue={setFetchCopyDocumentName}
          enableSearch={true}
        />
        <FilterDropdown
          placeholder="Case Type"
          options={caseTypeOptions}
          value={fetchCopyCaseType}
          setValue={setFetchCopyCaseType}
          disabled={loadingcaseType}
        />
        <button
          type="button"
          onClick={fetchFetchCopyMappedDocs}
          disabled={loadingFetchCopyDocs}
          className="rounded-[54px] border border-solid border-[#5AC69F] px-[28px] py-[14px] bg-[#ffffff] font-lufga font-medium text-[15px] text-[#5AC69F] disabled:opacity-70 disabled:cursor-not-allowed hover:bg-[#F4FBF8] transition-colors flex items-center justify-center gap-2"
        >
          {loadingFetchCopyDocs ? (
            <>
              <span className="inline-block w-4 h-4 border-2 border-[#5AC69F] border-t-transparent rounded-full animate-spin" />
              Fetching...
            </>
          ) : (
            "Fetch and copy"
          )}
        </button>
      </div>

      {/* ================= TWO-COLUMN LAYOUT (after Fetch and copy from second filter) ================= */}
      {showTwoColumnLayout && (
        <>
          <div className="grid grid-cols-2 gap-12 mt-10">
            {/* Left column: Default Mapped Documents (from first/header filter) */}
            <div>
              <h2 className="font-lufga font-medium text-[28px] mb-8">
                Default Mapped Documents
              </h2>
              {shouldShowSection("admission") && (
                <MappedSection
                  title="ADMISSION"
                  documents={admissionDocuments}
                  onAdd={() => {
                    setAddDocumentSection("admission");
                    setSelectedDocumentsForAdd([]);
                    setOpenAddDocumentModal(true);
                  }}
                  onDelete={(index) => {
                    setSelectedToRemove([{ type: "admission", index }]);
                    setOpenConfirmModal(true);
                  }}
                />
              )}
              {shouldShowSection("enhancement") && (
                <MappedSection
                  title="ENHANCEMENT"
                  documents={enhancementDocuments}
                  onAdd={() => {
                    setAddDocumentSection("enhancement");
                    setSelectedDocumentsForAdd([]);
                    setOpenAddDocumentModal(true);
                  }}
                  onDelete={(index) => {
                    setSelectedToRemove([{ type: "enhancement", index }]);
                    setOpenConfirmModal(true);
                  }}
                />
              )}
              {shouldShowSection("discharge") && (
                <MappedSection
                  title="DISCHARGE / CLAIM"
                  documents={dischargeDocuments}
                  onAdd={() => {
                    setAddDocumentSection("discharge");
                    setSelectedDocumentsForAdd([]);
                    setOpenAddDocumentModal(true);
                  }}
                  onDelete={(index) => {
                    setSelectedToRemove([{ type: "discharge", index }]);
                    setOpenConfirmModal(true);
                  }}
                />
              )}
            </div>

            {/* Right column: Fetch and Copy Documents from existing (from second filter) */}
            <div>
              <h2 className="font-lufga font-medium text-[22px] text-gray-700 mb-8">
                Fetch and Copy Documents from existing
              </h2>
              {shouldShowSection("admission") && (
                <FetchCopySection
                  title="ADMISSION"
                  documents={fetchCopyAdmissionDocuments}
                  existingDocuments={admissionDocuments}
                  onCopy={(doc) => handleCopyFromFetchCopy("admission", doc)}
                  onMergeAll={() => handleMergeSection("admission")}
                />
              )}
              {shouldShowSection("enhancement") && (
                <FetchCopySection
                  title="ENHANCEMENT"
                  documents={fetchCopyEnhancementDocuments}
                  existingDocuments={enhancementDocuments}
                  onCopy={(doc) => handleCopyFromFetchCopy("enhancement", doc)}
                  onMergeAll={() => handleMergeSection("enhancement")}
                />
              )}
              {shouldShowSection("discharge") && (
                <FetchCopySection
                  title="DISCHARGE / CLAIM"
                  documents={fetchCopyDischargeDocuments}
                  existingDocuments={dischargeDocuments}
                  onCopy={(doc) => handleCopyFromFetchCopy("discharge", doc)}
                  onMergeAll={() => handleMergeSection("discharge")}
                />
              )}
            </div>
          </div>

          <div className="flex flex-wrap justify-center gap-6 mt-12">
            <button
              type="button"
              onClick={handleMergeAll}
              className="border-2 border-[#7AD3AE] rounded-full px-12 py-4 font-lufga text-[16px] flex items-center gap-2 hover:opacity-90"
            >
              <img src={mergeIcon} alt="merge" className="w-5 h-5" />
              Merge All
            </button>
            <button
              type="button"
              onClick={handleReplaceAll}
              className="border-2 border-[#7AD3AE] rounded-full px-12 py-4 font-lufga text-[16px] flex items-center gap-2 hover:opacity-90"
            >
              <img src={mergeIcon} alt="replace" className="w-5 h-5" />
              Replace All
            </button>
          </div>
        </>
      )}

      {/* ================= SAVE (when pending from header OR newly merged from second column) ================= */}
      {(() => {
        const hasPending = (pendingDocumentsToMap || []).length > 0;
        const allCurrent = [
          ...(admissionDocuments || []),
          ...(enhancementDocuments || []),
          ...(dischargeDocuments || []),
        ];
        const initialIds = initiallyLoadedDocIdsRef.current;
        const initialKeys = initiallyLoadedNameSubTypeKeysRef.current;
        const hasNewlyMerged = allCurrent.some((doc) => {
          const id = getDocMappingId(doc);
          const key = getDocNameSubTypeKey(doc);
          if (id && initialIds.has(id)) return false;
          if (key && initialKeys.has(key)) return false;
          return true;
        });
        return hasPending || hasNewlyMerged;
      })() && (
      <div className="flex justify-center mt-12">
        <button
          type="button"
          onClick={handleSaveAllSections}
          disabled={savingFromHeader}
          className="w-[380px] h-[88px] rounded-[60px] bg-[#7AD3AE] border-2 border-solid border-[#5AC69F] font-lufga font-medium text-[16px] disabled:opacity-70 disabled:cursor-not-allowed hover:bg-[#5AC69F] transition-colors flex items-center justify-center gap-2"
        >
          {savingFromHeader ? (
            <>
              <span className="inline-block w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
              Saving...
            </>
          ) : (
            "Save"
          )}
        </button>
      </div>
      )}

      {/* ================= ADD DOCUMENT MODAL ================= */}
      <AddDocumentModal
        open={openAddDocumentModal}
        onClose={() => {
          setOpenAddDocumentModal(false);
          setAddDocumentSection(null);
          setSelectedDocumentsForAdd([]);
        }}
        section={addDocumentSection}
        selectedDocuments={selectedDocumentsForAdd}
        setSelectedDocuments={setSelectedDocumentsForAdd}
        admissionDocuments={admissionDocuments}
        enhancementDocuments={enhancementDocuments}
        dischargeDocuments={dischargeDocuments}
        isDocAlreadyInSection={isDocAlreadyInSection}
        schemeOptions={schemeOptions}
        selectedScheme={selectedScheme}
        setSelectedScheme={setSelectedScheme}
        loadingSchemes={loadingSchemes}
        specialityTypeOptions={specialityTypeOptions}
        specialityLabelMap={specialityLabelMap}
        selectedSpecialty={selectedSpecialty}
        setSelectedSpecialty={setSelectedSpecialty}
        documentTypeOptions={documentTypeOptions}
        documentTypeLabelMap={documentTypeLabelMap}
        selectedDocumentType={selectedDocumentType}
        setSelectedDocumentType={setSelectedDocumentType}
        loadingDocumentTypes={loadingDocumentTypes}
        caseTypeOptions={caseTypeOptions}
        selectedCaseType={selectedCaseType}
        setSelectedCaseType={setSelectedCaseType}
        loadingcaseType={loadingcaseType}
        documentNameList={documentNameList}
        loadingDocumentNames={loadingDocumentNames}
        saving={savingMapping}
        onAdd={handleAddDocumentsToSection}
      />

      {/* ================= REMOVE MAPPING POPUP ================= */}
      <RemoveMapping
        open={openRemoveModal}
        onClose={() => setOpenRemoveModal(false)}
        onProceed={(selectedIds) => {
          setSelectedToRemove(selectedIds);
          setOpenConfirmModal(true);
        }}
      />

      <ConfirmRemoveMapping
        open={openConfirmModal}
        onClose={() => {
          setOpenConfirmModal(false);
          setSelectedToRemove([]);
        }}
        onConfirm={handleConfirmRemoveMapping}
        loading={removingMapping}
      />
    </div>
  );
}

/* ===================================================== */
/* ================= REUSABLE COMPONENTS ================= */
/* ===================================================== */

function SelectField({ label, onSelect }) {
  return (
    <div
      onClick={onSelect}
      className="flex items-center justify-between min-w-[220px] h-[56px] rounded-[100px] px-[24px] bg-white border border-[#E8E8E899] font-lufga text-[15px] text-gray-700 cursor-pointer"
    >
      {label}
      <img src={downIcon} alt="dropdown" className="w-4 h-4" />
    </div>
  );
}

/* ================= ADD DOCUMENT MODAL ================= */

function AddDocumentModal({
  open,
  onClose,
  section,
  selectedDocuments,
  setSelectedDocuments,
  admissionDocuments,
  enhancementDocuments,
  dischargeDocuments,
  isDocAlreadyInSection,
  schemeOptions,
  selectedScheme,
  setSelectedScheme,
  loadingSchemes,
  specialityTypeOptions,
  specialityLabelMap,
  selectedSpecialty,
  setSelectedSpecialty,
  documentTypeOptions,
  documentTypeLabelMap,
  selectedDocumentType,
  setSelectedDocumentType,
  loadingDocumentTypes,
  caseTypeOptions,
  selectedCaseType,
  setSelectedCaseType,
  loadingcaseType,
  documentNameList,
  loadingDocumentNames,
  saving,
  onAdd,
}) {
  const [search, setSearch] = useState("");
  const prevOpenRef = useRef(false);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "auto";
    if (open && !prevOpenRef.current) {
      setSearch("");
      setSelectedDocuments([]);
      prevOpenRef.current = true;
    }
    if (!open) prevOpenRef.current = false;
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [open, setSelectedDocuments]);

  if (!open) return null;

  const sectionTitle =
    section === "admission"
      ? "ADMISSION"
      : section === "enhancement"
        ? "ENHANCEMENT"
        : section === "discharge"
          ? "DISCHARGE / CLAIM"
          : "";

  const getDocLabel = (d) =>
    typeof d === "string" ? d : d?.documentName ?? d?.name ?? d?.value ?? "";

  const getDocId = (d) => {
    if (!d) return "";
    if (typeof d === "string") return d;
    const id =
      d.documentTypeMappingId ??
      d.documentId ??
      d.id ??
      (d.documentName && d.documentSubType
        ? `${d.documentName}|${d.documentSubType}`
        : d.documentName ?? d.name ?? "");
    return String(id || "");
  };

  const filteredList = (documentNameList || []).filter((d) =>
    getDocLabel(d).toLowerCase().includes((search || "").toLowerCase()),
  );

  const isSelected = (doc) => {
    if (!Array.isArray(selectedDocuments) || selectedDocuments.length === 0)
      return false;
    const id = getDocId(doc);
    if (!id) return false;
    return selectedDocuments.some((d) => getDocId(d) === id);
  };

  const toggleDoc = (doc) => {
    const id = getDocId(doc);
    if (!id) return;
    if (section && typeof isDocAlreadyInSection === "function" && isDocAlreadyInSection(doc, section)) {
      toast.warn("This document is already mapped in this section.");
      return;
    }
    setSelectedDocuments((prev) => {
      const list = Array.isArray(prev) ? prev : [];
      const exists = list.some((d) => getDocId(d) === id);
      if (exists) {
        return list.filter((d) => getDocId(d) !== id);
      }
      return [...list, doc];
    });
  };

  const removeDoc = (doc) => {
    const id = getDocId(doc);
    setSelectedDocuments((prev) =>
      (Array.isArray(prev) ? prev : []).filter((d) => getDocId(d) !== id),
    );
  };

  const canAdd =
    section &&
    Array.isArray(selectedDocuments) &&
    selectedDocuments.length > 0 &&
    !loadingDocumentNames &&
    !saving;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-[#00000066] px-4">
      <div className="relative w-[80vw] max-w-[80vw] bg-white rounded-[24px] px-8 py-8 max-h-[80vh] flex flex-col">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-8 right-6 p-0 border-none bg-transparent"
        >
          <img src={closeIcon} alt="close" className="w-5 h-5" />
        </button>

        <h2 className="font-lufga font-medium text-[20px] text-[#494949] pr-10">
          Add document to {sectionTitle}
        </h2>

        <p className="font-lufga text-[14px] text-gray-500 mt-1 mb-4">
          Choose filters and select a document to add to this section.
        </p>

        <div className="flex flex-wrap gap-4 mb-4">
          <FilterDropdown
            placeholder="Scheme"
            options={schemeOptions}
            disabled={loadingSchemes}
            value={selectedScheme}
            setValue={setSelectedScheme}
          />
          <FilterDropdown
            placeholder="Speciality"
            options={specialityTypeOptions}
            value={selectedSpecialty}
            setValue={setSelectedSpecialty}
            clearValue={null}
            labelMap={specialityLabelMap}
            valueTitle={selectedSpecialty}
          />
          <FilterDropdown
            placeholder="Document Type"
            options={documentTypeOptions}
            disabled={loadingDocumentTypes}
            value={selectedDocumentType}
            setValue={setSelectedDocumentType}
            labelMap={documentTypeLabelMap}
          />
          <FilterDropdown
            placeholder="Case Type"
            options={caseTypeOptions}
            value={selectedCaseType}
            setValue={setSelectedCaseType}
            disabled={loadingcaseType}
          />
        </div>

        {Array.isArray(selectedDocuments) && selectedDocuments.length > 0 && (
          <div className="mb-4">
            <p className="font-lufga text-[13px] font-medium text-gray-600 mb-2">
              Selected documents ({selectedDocuments.length})
            </p>
            <div className="flex flex-wrap gap-2 max-h-[120px] overflow-y-auto p-2 bg-[#F4FBF8] rounded-xl border border-[#CDEFE2]">
            {selectedDocuments.map((doc, index) => {
              const label = getDocLabel(doc);
              const id = getDocId(doc);
              return (
                <span
                  key={`${id || label}-${index}`}
                  className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#F4FBF8] border border-[#CDEFE2] font-lufga text-[12px] text-[#1F6B4B] max-w-full"
                >
                  <span className="truncate max-w-[220px]">{label || "—"}</span>
                  <button
                    type="button"
                    onClick={() => removeDoc(doc)}
                    className="ml-1 text-xs text-[#1F6B4B] hover:text-[#0F3A29]"
                  >
                    ×
                  </button>
                </span>
              );
            })}
            </div>
          </div>
        )}

        <div className="flex-1 overflow-hidden flex flex-col min-h-0">
          {!selectedDocumentType ? (
            <p className="font-lufga text-[14px] text-gray-500 py-4">
              Select a document type to see documents.
            </p>
          ) : (
            <>
              <div className="relative mb-3 flex-shrink-0">
                <img
                  src={searchIcon}
                  alt="search"
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search document name..."
                  className="w-full pl-10 pr-4 py-3 rounded-full border border-[#E8E8E8] font-lufga text-[14px]"
                />
              </div>

              <div className="border border-[#E8E8E8] rounded-xl overflow-hidden flex-1 overflow-y-auto min-h-0">
              {loadingDocumentNames ? (
                <div className="py-12 text-center font-lufga text-[14px] text-gray-500">
                  Loading documents...
                </div>
              ) : filteredList.length === 0 ? (
                <div className="py-12 text-center font-lufga text-[14px] text-gray-500">
                  No documents found.
                </div>
              ) : (
                filteredList.map((doc, index) => {
                  const label = getDocLabel(doc);
                  const selected = isSelected(doc);
                  return (
                    <div
                      key={`${label}-${index}`}
                      role="button"
                      tabIndex={0}
                      onClick={() => toggleDoc(doc)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          toggleDoc(doc);
                        }
                      }}
                      className={`px-4 py-[1px] font-lufga text-[14px] leading-[155%] cursor-pointer border-b border-[#F0F0F0] last:border-b-0 break-words ${
                        selected ? "bg-[#F4FBF8] text-[#38B58A]" : "hover:bg-[#F9F9F9]"
                      }`}
                    >
                      {label || "—"}
                    </div>
                  );
                })
              )}
              </div>

              <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-[#E8E8E8] flex-shrink-0">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-full px-6 py-3 font-lufga text-[15px] border border-[#E8E8E8] bg-white hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={onAdd}
                  disabled={!canAdd}
                  className="rounded-full px-6 py-3 font-lufga text-[15px] bg-[#7AD3AE] border border-[#5AC69F] disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#5AC69F] transition-colors"
                >
                  {saving ? "Saving..." : `Add to ${sectionTitle}`}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ================= SCREEN 2 SECTION ================= */

function MappedSection({ title, documents = [], onAdd, onDelete }) {
  const isEmpty = !Array.isArray(documents) || documents.length === 0;
  return (
    <div className="mb-10">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-lufga font-semibold text-[16px]">{title}</h3>

        <button
          type="button"
          onClick={() => typeof onAdd === "function" && onAdd()}
          className="w-[36px] h-[36px] flex items-center justify-center hover:opacity-90 p-0 border-0 bg-transparent"
          title="Add document"
        >
          <img src={addButtonSymbol} alt="add" className="w-full h-full object-contain" />
        </button>
      </div>

      <div className="max-h-[280px] overflow-y-auto rounded-xl border border-[#E8E8E8] bg-[#F3FAF7]">
        {isEmpty ? (
          <div className="py-6 px-4 rounded-xl border-0 border-dashed border-gray-200 bg-[#F3FAF7]">
            <p className="font-lufga text-[14px] text-gray-500 text-center">
              No documents mapped
            </p>
          </div>
        ) : (
          documents.map((doc, index) => (
            <div
              key={index}
              className="flex items-start justify-between gap-6 px-4 py-1.5 border-b border-[#F0F0F0] last:border-b-0"
            >
              <p className="font-lufga text-[14px] leading-[155%] max-w-[90%] break-words">
                {typeof doc === "string"
                  ? doc
                  : doc?.documenttypemapping?.documentName ??
                    doc?.name ??
                    doc?.documentName ??
                    doc?.documentTitle ??
                    doc?.label ??
                    doc?.value ??
                    doc?.title ??
                    "—"}
              </p>
              <img
                src={deleteIcon}
                alt="delete"
                className="w-5 h-5 cursor-pointer flex-shrink-0"
                onClick={() => typeof onDelete === "function" && onDelete(index)}
              />
            </div>
          ))
        )}
      </div>
    </div>
  );
}

/* ================= FETCH AND COPY RIGHT COLUMN SECTION ================= */

function FetchCopySection({ title, documents = [], existingDocuments = [], onCopy, onMergeAll }) {
  const getDocLabel = (doc) =>
    typeof doc === "string"
      ? doc
      : doc?.documenttypemapping?.documentName ??
        doc?.documentName ??
        doc?.name ??
        doc?.documentTitle ??
        doc?.label ??
        doc?.value ??
        doc?.title ??
        "—";

  const getDocId = (doc) =>
    String(
      (typeof doc === "object" && doc !== null
        ? doc.documentTypeMappingId ??
          doc.documenttypemapping?.documentTypeMappingId ??
          doc.documenttypemapping?.documentId ??
          doc.documentId ??
          doc.id ??
          doc.documentName ??
          doc.name ??
          ""
        : doc) || "",
    );

  const getDocNameSubTypeKey = (doc) => {
    if (!doc || typeof doc !== "object") return "";
    const name =
      doc.documenttypemapping?.documentName ??
      doc.documentName ??
      doc.name ??
      "";
    const subType =
      doc.documenttypemapping?.documentSubType ??
      doc.documentSubType ??
      doc.documentSubtype ??
      "";
    return `${String(name).trim()}|${String(subType).trim()}`;
  };

  const existingIds = new Set();
  const existingNameSubTypeKeys = new Set();
  (Array.isArray(existingDocuments) ? existingDocuments : []).forEach((d) => {
    const id = getDocId(d);
    if (id) existingIds.add(id);
    const key = getDocNameSubTypeKey(d);
    if (key) existingNameSubTypeKeys.add(key);
  });

  const isAlreadyPresent = (doc) => {
    const id = getDocId(doc);
    if (id && existingIds.has(id)) return true;
    const key = getDocNameSubTypeKey(doc);
    return key ? existingNameSubTypeKeys.has(key) : false;
  };

  const isEmpty = !Array.isArray(documents) || documents.length === 0;
  const canMergeAll = typeof onMergeAll === "function" && !isEmpty;
  return (
    <div className="mb-10">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-lufga font-semibold text-[16px]">
          {title} ({documents.length})
        </h3>
        <button
          type="button"
          onClick={onMergeAll}
          disabled={!canMergeAll}
          title={`Merge all ${title} documents to Default Mapped Documents`}
          className="flex items-center gap-1.5 rounded-full border border-[#5AC69F] px-3 py-1.5 font-lufga text-[13px] text-[#5AC69F] hover:bg-[#F4FBF8] disabled:opacity-50 disabled:cursor-not-allowed disabled:border-gray-300 disabled:text-gray-400"
        >
          <img src={mergeIcon} alt="merge" className="w-4 h-4" />
          Merge all
        </button>
      </div>
      <div className="max-h-[280px] overflow-y-auto rounded-xl border border-[#E8E8E8] bg-[#F3FAF7]">
        {isEmpty ? (
          <div className="py-6 px-4 rounded-xl border-0 border-dashed border-gray-200 bg-[#F3FAF7]">
            <p className="font-lufga text-[14px] text-gray-500 text-center">
              No documents
            </p>
          </div>
        ) : (
          documents.map((doc, index) => {
            const alreadyExists = isAlreadyPresent(doc);
            return (
              <div
                key={index}
                className={`flex items-start justify-between gap-4 px-4 py-1.5 border-b border-[#F0F0F0] last:border-b-0 ${alreadyExists ? "bg-[#E8F5E9]/60" : ""}`}
              >
                <div className="flex items-start gap-2 max-w-[85%]">
                  {alreadyExists && (
                    <span
                      className="flex-shrink-0 w-5 h-5 mt-0.5 rounded-full bg-[#4CAF50] flex items-center justify-center"
                      title="Already present in Default Mapped Documents"
                    >
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path d="M2.5 6L5 8.5L9.5 3.5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </span>
                  )}
                  <p className={`font-lufga text-[14px] leading-[155%] break-words ${alreadyExists ? "text-gray-500" : ""}`}>
                    {getDocLabel(doc)}
                  </p>
                </div>
                {alreadyExists ? (
                  <span
                    className="flex-shrink-0 font-lufga text-[11px] text-[#4CAF50] bg-[#E8F5E9] border border-[#4CAF50]/30 rounded-full px-2.5 py-1 whitespace-nowrap mt-0.5"
                    title="This document is already mapped"
                  >
                    Already mapped
                  </span>
                ) : (
                  <span
                    className="flex-shrink-0 w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center cursor-pointer hover:bg-[#F4FBF8] hover:border-[#5AC69F] hover:text-[#5AC69F]"
                    title="Copy to Default Mapped Documents"
                    role="button"
                    tabIndex={0}
                    onClick={() => typeof onCopy === "function" && onCopy(doc)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        if (typeof onCopy === "function") onCopy(doc);
                      }
                    }}
                  >
                    <img src={mergeIcon} alt="merge" className="w-4 h-4" />
                  </span>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

/* ================= SCREEN 3 ================= */

function CompareColumn({ title, data, type }) {
  return (
    <div>
      <h2 className="font-lufga font-medium text-[22px] mb-6">{title}</h2>

      {Object.entries(data).map(([key, docs]) => (
        <CompareSection key={key} title={key} documents={docs} type={type} />
      ))}
    </div>
  );
}

function CompareSection({ title, documents, type }) {
  return (
    <div className="mb-8">
      <div className="flex justify-between mb-3">
        <h3 className="font-lufga font-semibold">
          {title.toUpperCase()} ({documents.length})
        </h3>
        <span className="cursor-pointer">{type === "current" ? "+" : <img src={mergeIcon} alt="merge" className="w-4 h-4 inline" />}</span>
      </div>

      {documents.map((doc, index) => (
        <div key={index} className="flex justify-between mb-3">
          <p className="text-[14px] max-w-[85%]">{doc}</p>
          <span className="cursor-pointer">
            {type === "current" ? "🗑" : <img src={mergeIcon} alt="merge" className="w-4 h-4 inline" />}
          </span>
        </div>
      ))}
    </div>
  );
}

/* ================= STATIC DATA (e.g. for Fetch & Copy – existing mapping) ================= */

const existingMappedDocuments = {
  admission: [
    "Admission Date Geo-Tagged Photo of the patient",
    "X-ray / MRI / CT of affected part – labelled with patient name and date",
  ],
  enhancement: [
    "All Blood and Radiology Investigation Reports including CAG report (From Admission to Discharge)",
  ],
  discharge: ["Discharge Summary"],
};
